import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Button } from "@/modules/shared/ui/components/button"
import { Label } from "@/modules/shared/ui/components/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { Input } from "@/modules/shared/ui/components/input"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { userService } from "@/modules/users/infrastructure/user-service"
import {
  DocumentPermissionType,
  DOCUMENT_PERMISSION_OPTIONS,
} from "../../dtos/DocumentPermissionType"
import { GrantDocumentPermissionRequest } from "../../dtos/DocumentPermissionDto"
import { GrantFolderPermissionRequest } from "../../dtos/FolderPermissionDto"

type GrantRequest =
  | GrantDocumentPermissionRequest
  | GrantFolderPermissionRequest

interface GrantPermissionFormProps {
  onGrant: (request: GrantRequest) => void
  isPending: boolean
}

export function GrantPermissionForm({
  onGrant,
  isPending,
}: GrantPermissionFormProps) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  const [subjectType, setSubjectType] = useState<"user" | "role">("user")
  const [selectedUserId, setSelectedUserId] = useState("")
  const [selectedRoleId, setSelectedRoleId] = useState("")
  const [emailFilter, setEmailFilter] = useState("")
  const [permissionType, setPermissionType] =
    useState<DocumentPermissionType>("Read")
  const [validationError, setValidationError] = useState("")

  const { data: usersData } = useQuery({
    queryKey: ["org-users", orgId],
    queryFn: () => userService.getOrganizationUsers(orgId),
    enabled: !!orgId,
  })

  const { data: rolesData } = useQuery({
    queryKey: ["org-roles", orgId],
    queryFn: () => userService.getOrganizationRoles(orgId),
    enabled: !!orgId,
  })

  const filteredUsers = (usersData?.users ?? []).filter(
    (u) =>
      !emailFilter ||
      u.email.toLowerCase().includes(emailFilter.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(emailFilter.toLowerCase()),
  )

  const handleSubmit = () => {
    setValidationError("")

    if (subjectType === "user" && !selectedUserId) {
      setValidationError("Please select a user.")
      return
    }
    if (subjectType === "role" && !selectedRoleId) {
      setValidationError("Please select a role.")
      return
    }

    onGrant({
      userId: subjectType === "user" ? selectedUserId : null,
      roleId: subjectType === "role" ? selectedRoleId : null,
      permissionType,
    })

    // Reset
    setSelectedUserId("")
    setSelectedRoleId("")
    setEmailFilter("")
  }

  return (
    <div className="space-y-4">
      {/* Subject type toggle */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant={subjectType === "user" ? "default" : "outline"}
          size="sm"
          onClick={() => setSubjectType("user")}
        >
          User
        </Button>
        <Button
          type="button"
          variant={subjectType === "role" ? "default" : "outline"}
          size="sm"
          onClick={() => setSubjectType("role")}
        >
          Role
        </Button>
      </div>

      {subjectType === "user" ? (
        <div className="space-y-2">
          <Label>Search user by name or email</Label>
          <Input
            placeholder="Filter by email or name…"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a user" />
            </SelectTrigger>
            <SelectContent>
              {filteredUsers.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Role</Label>
          <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {(rolesData?.roles ?? []).map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        <Label>Permission type</Label>
        <Select
          value={permissionType}
          onValueChange={(v) => setPermissionType(v as DocumentPermissionType)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DOCUMENT_PERMISSION_OPTIONS.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {validationError && (
        <p className="text-sm text-destructive">{validationError}</p>
      )}

      <Button onClick={handleSubmit} disabled={isPending} className="w-full">
        {isPending ? "Granting…" : "Grant Permission"}
      </Button>
    </div>
  )
}
